import { useContext } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { datadogLogs } from '@datadog/browser-logs';
import { Button, FormControl, FormLabel, Stack, TextField } from '@mui/material';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import useAlertTraining from 'src/hooks/useAlertTraining';
import { selectFacility, selectStaffMemberId } from 'src/store/configSlice';
import {
    Task,
    useAddCareEventNoteMutation,
    useCreateNoteForTaskMutation,
} from 'src/store/sageAdminApi';
import { snackActions } from 'src/utils/snackBarTools';

type FormState = {
    notes: string;
};

interface AddTaskNoteDrawerBaseProps extends DrawerComponentProps {
    cancelButtonLabel?: string;
    characterLimit?: number;
    isRequired?: boolean;
    pageName?: string;
    showCancelAction?: boolean;
    submitButtonLabel?: string;
    title?: string;
}

interface AddTaskNoteDrawerTaskProps extends AddTaskNoteDrawerBaseProps {
    taskId: string;
    task?: Task;
    noteSubject?: string;
    careEventId?: never;
}

interface AddTaskNoteDrawerCareEventProps extends AddTaskNoteDrawerBaseProps {
    careEventId: string;
    taskId?: never;
    task?: never;
    noteSubject?: never;
}

export type AddTaskNoteDrawerProps = AddTaskNoteDrawerTaskProps | AddTaskNoteDrawerCareEventProps;

function AddTaskNoteDrawer(props: AddTaskNoteDrawerProps) {
    const {
        cancelButtonLabel = 'Cancel',
        characterLimit = 255,
        isRequired = false,
        onPrevious,
        onNext,
        pageName,
        showCancelAction = true,
        submitButtonLabel = 'Submit',
        title = 'Add a Note',
    } = props;

    const isCareEventNote = 'careEventId' in props && props.careEventId != null;

    const { trackEvent } = useContext(AnalyticsContext);
    const staffMemberProfileId = useSelector(selectStaffMemberId);
    const facility = useSelector(selectFacility);

    const [addTaskNote, { isLoading: isCreatingNote }] = useCreateNoteForTaskMutation();
    const [addCareEventNote, { isLoading: isAddingCareEventNote }] = useAddCareEventNoteMutation();

    const { control, handleSubmit, formState } = useForm<FormState>({
        mode: 'onChange',
        defaultValues: { notes: '' },
    });

    const { addNoteToTrainingAlert, isTrainingAlert } = useAlertTraining({
        taskRid: isCareEventNote ? undefined : props.taskId,
    });

    const notes = useWatch({ control, name: 'notes' });
    const difference = Math.abs((notes?.length ?? 0) - characterLimit);

    const shouldDisableActions =
        (isRequired ? !formState.isDirty || !formState.isValid : !formState.isValid) ||
        isCreatingNote ||
        isAddingCareEventNote;

    const handleGoBack = () => {
        onPrevious(true, ['notes']);
    };

    const validateNoteValue = (value: string) => {
        if (isRequired && !value.length) {
            return 'This field is required';
        }

        const difference = Math.abs(value.length - characterLimit);
        return value.length > characterLimit ? `${difference} characters over the limit` : true;
    };

    const handleSubmitCareEventNotes = async (data: FormState) => {
        if (!isCareEventNote) return;
        const { careEventId } = props;
        trackEvent('Add Care Event Note Clicked', {
            objectType: ObjectTypes.Button,
            pageName,
            careEventId,
            buttonText: submitButtonLabel,
        });

        try {
            await addCareEventNote({
                careEventId,
                facilityId: facility.id!,
                createCareEventNoteRequest: {
                    noteType: 'CAREGIVER_NOTE',
                    content: data.notes,
                    creatorRid: staffMemberProfileId,
                },
            }).unwrap();

            snackActions.success('Note added successfully');
            onNext();
        } catch (error: any) {
            datadogLogs.logger.error(
                'Failed to add note',
                {
                    noteLength: data.notes.length,
                    pageName,
                    careEventId,
                },
                error
            );
            snackActions.error('Failed to add note');
        }
    };

    const handleSubmitNotes = async (data: FormState) => {
        const { notes } = data;

        if (isCareEventNote) {
            return handleSubmitCareEventNotes(data);
        }
        const { taskId, task, noteSubject = 'Task Note' } = props;

        if (!isRequired && !notes.trim()) {
            onNext();
            return;
        }
        trackEvent('Add Task Note Clicked', {
            objectType: ObjectTypes.Button,
            pageName,
            taskStatus: task?.status,
            taskSeverity: task?.severity,
            taskId,
            buttonText: submitButtonLabel,
            isTrainingAlert,
        });

        try {
            if (isTrainingAlert) {
                addNoteToTrainingAlert(notes);
            } else {
                await addTaskNote({
                    taskId,
                    createOrUpdateTaskNoteRequest: { subject: noteSubject, content: notes },
                }).unwrap();
            }

            snackActions.success('Note added successfully');
            onNext();
        } catch (error: any) {
            datadogLogs.logger.error(
                'Failed to add note',
                {
                    noteLength: data.notes.length,
                    pageName,
                    taskId,
                    taskStatus: task?.status,
                    taskSeverity: task?.severity,
                },
                error
            );
            snackActions.error('Failed to add note');
        }
    };

    return (
        <>
            <DrawerTitle title={title} />

            <form onSubmit={handleSubmit(handleSubmitNotes)}>
                <DrawerBody>
                    <FormControl variant="outlined">
                        <Stack sx={{ gap: 0.5 }}>
                            <FormLabel required={isRequired} htmlFor="notes-input">
                                Enter notes here
                            </FormLabel>
                            <Controller
                                name="notes"
                                control={control}
                                rules={{ validate: validateNoteValue }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        id="notes-input"
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        error={!!fieldState.error?.message}
                                        helperText={
                                            fieldState.error?.message ||
                                            `${difference} characters remaining`
                                        }
                                    />
                                )}
                            />
                        </Stack>
                    </FormControl>
                </DrawerBody>

                <DrawerFooter sx={{ gap: 2 }}>
                    <Button
                        fullWidth
                        color="primary"
                        type="submit"
                        variant="contained"
                        loading={isCreatingNote || isAddingCareEventNote}
                        disabled={shouldDisableActions}
                        disableElevation
                    >
                        {submitButtonLabel}
                    </Button>

                    {showCancelAction && (
                        <Button color="primary" disableElevation fullWidth onClick={handleGoBack}>
                            {cancelButtonLabel}
                        </Button>
                    )}
                </DrawerFooter>
            </form>
        </>
    );
}

export default AddTaskNoteDrawer;
